import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Briefcase, MapPin, Calendar, Users, Wifi, Car, Coffee, 
  Shield, Clock, CheckCircle, ArrowRight, Phone, Building2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

const features = [
  { icon: Wifi, title: 'High-Speed WiFi', description: 'Reliable internet for work' },
  { icon: Car, title: 'Parking Included', description: 'Secure parking spaces' },
  { icon: Coffee, title: 'Fully Equipped Kitchen', description: 'Modern appliances' },
  { icon: Shield, title: '24/7 Security', description: 'Safe and secure' },
  { icon: Clock, title: 'Flexible Duration', description: '1 week to 6 months' },
  { icon: Users, title: 'Housekeeping', description: 'Weekly cleaning included' },
];

const benefits = [
  'Fully furnished premium apartments',
  'Move-in ready with all utilities included',
  'Dedicated account manager',
  'Flexible lease terms',
  'Corporate billing and invoicing',
  'Airport pickup service available',
];

const CorporateRentals = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    city: 'Hyderabad',
    duration: '1-month',
    occupants: '1',
    move_in_date: '',
    budget: '',
    requirements: ''
  });

  // Validation helpers
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    
    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Contact person is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone';
    }
    
    if (!formData.move_in_date) {
      newErrors.move_in_date = 'Move-in date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_line: 'corporate_rentals',
          name: formData.contact_name,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          message: formData.requirements,
          source: 'website',
          metadata: {
            company_name: formData.company_name,
            duration: formData.duration,
            occupants: formData.occupants,
            move_in_date: formData.move_in_date,
            budget: formData.budget
          }
        })
      });
      const data = await response.json();
      if (response.ok) {
        setSubmitted(true);
        toast.success('Request submitted successfully! Our team will contact you within 24-48 hours.');
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="corporate-rentals-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-primary/20 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000')] bg-cover bg-center" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Briefcase className="w-4 h-4" /> Corporate Solutions
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Executive Stays for Business Travelers
            </h1>
            <p className="text-lg text-gray-300 mb-8">
              Premium fully-furnished apartments for executives on the move. Short-term stays with long-term comfort.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button 
                size="lg"
                className="rounded-full bg-white text-gray-900 hover:bg-gray-100 px-8"
                onClick={() => document.getElementById('inquiry-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Request a Quote <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="rounded-full border-white/30 text-white hover:bg-white/10 px-8"
                onClick={() => document.getElementById('inquiry-form')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="talk-to-us-btn"
              >
                <Phone className="w-5 h-5 mr-2" /> Talk to Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Everything Your Team Needs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our corporate apartments come fully equipped with premium amenities for a productive and comfortable stay.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + Form */}
      <section className="py-20" id="inquiry-form">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Choose ZenStudios Corporate?</h2>
              <p className="text-gray-600 mb-8">
                We understand the unique needs of business travelers. Our corporate housing solutions offer the perfect blend of comfort, convenience, and professionalism.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-12 p-6 bg-primary/5 rounded-xl border border-primary/20">
                <h3 className="font-semibold mb-2">Bulk Booking Discounts</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Booking for multiple executives? Get special corporate rates and dedicated support.
                </p>
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%27m%20interested%20in%20bulk%20booking%20corporate%20rentals%20for%20my%20team.', '_blank')}
                  data-testid="contact-sales-btn"
                >
                  Contact Sales Team
                </Button>
              </div>
            </div>

            {/* Form */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-6">Request Corporate Housing</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => {
                        setFormData({...formData, company_name: e.target.value});
                        if (errors.company_name) setErrors({...errors, company_name: ''});
                      }}
                      placeholder="Acme Inc."
                      className={`rounded-xl mt-1 ${errors.company_name ? 'border-red-500' : ''}`}
                    />
                    {errors.company_name && <p className="text-red-500 text-xs mt-1">{errors.company_name}</p>}
                  </div>
                  <div>
                    <Label>Contact Person *</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => {
                        setFormData({...formData, contact_name: e.target.value});
                        if (errors.contact_name) setErrors({...errors, contact_name: ''});
                      }}
                      placeholder="John Doe"
                      className={`rounded-xl mt-1 ${errors.contact_name ? 'border-red-500' : ''}`}
                    />
                    {errors.contact_name && <p className="text-red-500 text-xs mt-1">{errors.contact_name}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({...formData, email: e.target.value});
                        if (errors.email) setErrors({...errors, email: ''});
                      }}
                      placeholder="john@company.com"
                      className={`rounded-xl mt-1 ${errors.email ? 'border-red-500' : ''}`}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({...formData, phone: e.target.value});
                        if (errors.phone) setErrors({...errors, phone: ''});
                      }}
                      placeholder="+91 98765 43210"
                      className={`rounded-xl mt-1 ${errors.phone ? 'border-red-500' : ''}`}
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Select value={formData.city} onValueChange={(v) => setFormData({...formData, city: v})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                        <SelectItem value="Bangalore">Bangalore</SelectItem>
                        <SelectItem value="Mumbai">Mumbai</SelectItem>
                        <SelectItem value="Delhi">Delhi NCR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Duration *</Label>
                    <Select value={formData.duration} onValueChange={(v) => setFormData({...formData, duration: v})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-week">1 Week</SelectItem>
                        <SelectItem value="2-weeks">2 Weeks</SelectItem>
                        <SelectItem value="1-month">1 Month</SelectItem>
                        <SelectItem value="3-months">3 Months</SelectItem>
                        <SelectItem value="6-months">6 Months</SelectItem>
                        <SelectItem value="custom">Custom Duration</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Number of Occupants</Label>
                    <Select value={formData.occupants} onValueChange={(v) => setFormData({...formData, occupants: v})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['1', '2', '3', '4', '5+'].map(n => (
                          <SelectItem key={n} value={n}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Move-in Date *</Label>
                    <Input
                      type="date"
                      value={formData.move_in_date}
                      onChange={(e) => {
                        setFormData({...formData, move_in_date: e.target.value});
                        if (errors.move_in_date) setErrors({...errors, move_in_date: ''});
                      }}
                      className={`rounded-xl mt-1 ${errors.move_in_date ? 'border-red-500' : ''}`}
                    />
                    {errors.move_in_date && <p className="text-red-500 text-xs mt-1">{errors.move_in_date}</p>}
                  </div>
                </div>
                <Button type="submit" className="w-full rounded-full bg-primary py-6" data-testid="corporate-submit-btn">
                  Submit Request
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateRentals;
